import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import { AccountingMigrationResponse } from "@common/schemas/accounting-integration.schema";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";

export type MigrationSourceInfo = Pick<
    AccountingMigrationResponse,
    "externalSystem" | "integrationConnectionId" | "syncGeneralLedger"
>;

const QUICKBOOKS_ONLINE_EXTERNAL_SYSTEMS: AccountingMigrationExternalSystem[] =
    [
        AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE,
        AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE_GL_COA,
    ];

const NAMED_MIGRATION_EXTERNAL_SYSTEMS: AccountingMigrationExternalSystem[] = [
    ...QUICKBOOKS_ONLINE_EXTERNAL_SYSTEMS,
    AccountingMigrationExternalSystem.QUICKBOOKS_DESKTOP,
    AccountingMigrationExternalSystem.XERO,
];

interface GetHiddenJournalEntrySourceFiltersParams {
    hasQuickBooksConnection: boolean;
    migrations: MigrationSourceInfo[];
}

/**
 * Migration-source filter options are only shown when the workspace actually
 * has matching migrated data: a QuickBooks connection or QBO migration for
 * QuickBooks Online, a dedicated migration for QuickBooks Desktop / Xero, and
 * any other general-ledger migration system for the Migrated data fallback.
 */
export function getHiddenJournalEntrySourceFilters({
    hasQuickBooksConnection,
    migrations,
}: GetHiddenJournalEntrySourceFiltersParams): JournalEntrySourceFilter[] {
    const glMigrations = migrations.filter(
        (migration) => migration.syncGeneralLedger,
    );

    // Legacy connection-based QBO migrations can have a null externalSystem;
    // they are identified by their integration connection instead.
    const hasQuickBooksOnlineMigration = glMigrations.some(
        (migration) =>
            (migration.externalSystem !== null &&
                QUICKBOOKS_ONLINE_EXTERNAL_SYSTEMS.includes(
                    migration.externalSystem,
                )) ||
            (migration.externalSystem === null &&
                migration.integrationConnectionId !== null),
    );
    const hasQuickBooksDesktopMigration = glMigrations.some(
        (migration) =>
            migration.externalSystem ===
            AccountingMigrationExternalSystem.QUICKBOOKS_DESKTOP,
    );
    const hasXeroMigration = glMigrations.some(
        (migration) =>
            migration.externalSystem === AccountingMigrationExternalSystem.XERO,
    );
    const hasFallbackMigration = glMigrations.some(
        (migration) =>
            migration.externalSystem !== null &&
            !NAMED_MIGRATION_EXTERNAL_SYSTEMS.includes(
                migration.externalSystem,
            ),
    );

    const hiddenFilters: JournalEntrySourceFilter[] = [];
    if (!hasQuickBooksConnection && !hasQuickBooksOnlineMigration) {
        hiddenFilters.push(JournalEntrySourceFilter.QuickBooksOnline);
    }
    if (!hasQuickBooksDesktopMigration) {
        hiddenFilters.push(JournalEntrySourceFilter.QuickBooksDesktop);
    }
    if (!hasXeroMigration) {
        hiddenFilters.push(JournalEntrySourceFilter.Xero);
    }
    if (!hasFallbackMigration) {
        hiddenFilters.push(JournalEntrySourceFilter.MigratedData);
    }
    return hiddenFilters;
}
